<script lang="ts">
  // The top of a Sync page. Connected: a bar with the service, its status, any `actions` and
  // Disconnect (asked first). Not connected: what connecting does, with the `connect` button.
  // `warning` shows under either, for a connection that needs renewing.
  import { Button } from '$lib/components/ui/button'
  import * as Card from '$lib/components/ui/card'
  import * as Dialog from '$lib/components/ui/dialog'
  import ServiceMark from './ServiceMark.svelte'
  import { SERVICES, type SyncService } from './services'

  export let service: SyncService
  export let connected: boolean
  /** What connecting gives the athlete, shown while not connected. */
  export let benefits: string[] = []
  export let title = `Connect Xert with ${SERVICES[service].name}`
  export let warning = ''
  export let disconnectUrl: string
  /** What stops when the athlete disconnects. */
  export let disconnectMessage: string

  $: name = SERVICES[service].name
  $: guide = SERVICES[service].guide
  let disconnectOpen = false
</script>

{#if connected}
  <div class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
    <div class="flex items-center gap-3">
      <ServiceMark {service} />
      <div class="flex flex-col">
        <span class="text-base font-semibold text-foreground">{name}</span>
        <span class="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span class="inline-block h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
          Connected
        </span>
      </div>
    </div>
    <div class="flex flex-wrap items-center gap-2">
      {#if guide}
        <Button variant="ghost" size="sm" href={guide} target="_blank" rel="noopener">
          <i class="fa-solid fa-circle-question mr-2" aria-hidden="true" /> Setup guide
        </Button>
      {/if}
      <slot name="actions" />
      <Button variant="outline" size="sm" on:click={() => (disconnectOpen = true)}>
        <i class="fa-solid fa-link-slash mr-2" aria-hidden="true" /> Disconnect
      </Button>
    </div>
  </div>
{:else}
  <Card.Root class="overflow-hidden">
    <div class="flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between">
      <div class="flex items-start gap-4">
        <ServiceMark {service} size="lg" />
        <div class="flex flex-col gap-2">
          <h2 class="m-0 text-xl font-semibold text-foreground">{title}</h2>
          <ul class="m-0 flex list-none flex-col gap-1.5 p-0 text-sm text-muted-foreground">
            {#each benefits as benefit}
              <li><i class="fa-solid fa-check mr-2 text-green-600" aria-hidden="true" />{benefit}</li>
            {/each}
          </ul>
          {#if guide}
            <a href={guide} target="_blank" rel="noopener" class="text-sm font-medium text-primary underline-offset-2 hover:underline">
              <i class="fa-solid fa-circle-question mr-1.5" aria-hidden="true" />Setup guide
            </a>
          {/if}
        </div>
      </div>
      <div class="flex shrink-0 flex-col items-start gap-2 md:items-end">
        <slot name="connect" />
      </div>
    </div>
  </Card.Root>
{/if}

{#if warning}
  <div role="alert" class="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
    <i class="fa-solid fa-triangle-exclamation mt-0.5" aria-hidden="true" />
    <span>{warning}</span>
  </div>
{/if}

{#if connected}
  <Dialog.Root portal="#shadcn-ui-dialog-target" bind:open={disconnectOpen}>
    <Dialog.Content class="max-w-md">
      <Dialog.Header>
        <Dialog.Title>Disconnect {name}?</Dialog.Title>
        <Dialog.Description>{disconnectMessage}</Dialog.Description>
      </Dialog.Header>
      <Dialog.Footer class="gap-2">
        <Button variant="outline" on:click={() => (disconnectOpen = false)}>Cancel</Button>
        <Button variant="destructive" href={disconnectUrl}>Disconnect</Button>
      </Dialog.Footer>
    </Dialog.Content>
  </Dialog.Root>
{/if}
